import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigation, useSubmit } from 'react-router-dom';

import { openModal } from '../state/actions/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';
import { ChangePauseCauseConfirmPayload } from '../components/modals/submodals/ChangePauseCauseModal/types';
import { useSurveillanceRoutes } from './useSurveillanceRoutes';
import { createFormDataFromValues } from '../utils/forms';
import { showSuccessToast } from '../utils/toasts';
import i18n from '../i18n';
import {
  rememberFromSegment,
  selectableSegmentCauses,
} from '../utils/surveillanceRoute';

/** Order gemela del recorrido: el padre de la básica que se crea. */
export interface SegmentCauseParentOrder {
  id?: string | null;
  roid?: string | null;
}

/** Tramo al que se le lanza la cadena de la Actuación básica. */
export interface BasicActionChainTarget {
  /** Id de la ENTITY RouteSegment (no el de su Order gemela). */
  segmentId: string;
  routeId?: string | null;
  parentOrder?: SegmentCauseParentOrder | null;
}

/** Lo que necesita el modal para cambiar la causa de un tramo. */
export interface ChangeSegmentCauseTarget extends BasicActionChainTarget {
  currentCause: string;
  currentDetail?: string | null;
  /** ROID de la básica vinculada: el modal avisa de que se desvinculará. */
  linkedOrderRoid?: string | null;
  /**
   * ¿Se puede crear la básica en este recorrido? Sin eso no se ofrece
   * «Actuación» (`selectableSegmentCauses`). Lo decide quien abre el modal:
   * `canLinkBasicActionFromRoute` en la tabla, `canCreateBasicAction` en la
   * card.
   */
  canChooseAction: boolean;
}

/**
 * Cambiar la causa de un tramo (D6) — UNA sola lógica para sus dos puertas: la
 * etiqueta «Causa: X» de la card del tramo («Órdenes asociadas») y la columna
 * «Cambiar causa» de la tabla de Tramos («Vista previa», 2026-10-07).
 *
 * Abre `ChangePauseCauseModal`, guarda con `updateSegmentPauseCause` (el
 * backend reconcilia los vínculos) y, si la causa PASA a `action`, lanza la
 * cadena P9: crea la Actuación básica del tramo y entra en su asistente.
 *
 * Las causas que ofrece el modal salen de `selectableSegmentCauses` (15-oct):
 * «Actuación» solo si en ese recorrido se puede crear la básica.
 *
 * `onSaved` corre tras guardar (o encolar sin red) con el id del tramo
 * cuyo cambio se guardó: las páginas del RV leen el
 * snapshot del loader y sin revalidar seguirían enseñando la causa vieja.
 */
export const useChangeSegmentPauseCause = (
  onSaved?: (segmentId: string) => void
) => {
  const dispatch = useDispatch();
  const submit = useSubmit();
  const navigation = useNavigation();

  // Cadena de la básica EN CURSO (revisión M3): tarda segundos y un segundo
  // toque lanzaba otra `saveAction` —una básica sin vincular que bloquea
  // validar—. Se suelta cuando el router vuelve a estar quieto: la cadena
  // terminó (en el asistente) o falló (de vuelta con su aviso).
  const chainInFlight = useRef(false);
  useEffect(() => {
    if (navigation.state === 'idle') chainInFlight.current = false;
  }, [navigation.state]);
  const { updateSegmentPauseCause } = useSurveillanceRoutes();

  /**
   * Lanza la cadena P9: crea la Actuación básica del tramo y entra en su
   * asistente reducido, con auto-link vía `?fromSegment`.
   *
   * La usa también la pill «Vincular a actuación» de la card (2026-08-03): un
   * tramo que nació con causa Actuación y se quedó sin básica (cierre sin
   * cobertura, asistente abandonado) no tenía otra forma de recuperarla.
   *
   * `description` = detalle de la causa, que el asistente hereda como
   * descripción de la actuación.
   */
  const launchBasicActionChain = useCallback(
    (target: BasicActionChainTarget, description?: string | null) => {
      const { segmentId, routeId, parentOrder } = target;
      if (!segmentId || !routeId || !parentOrder?.id) return;
      if (chainInFlight.current) return;
      chainInFlight.current = true;
      const formData = createFormDataFromValues({
        id: parentOrder.id,
        roid: parentOrder.roid,
        // P9 auto-create (2026-07-21) — misma cadena que el modal de pausa:
        // básica auto-creada + asistente reducido directo.
        intent: 'create-basic-action',
        ...(description && { description }),
      });
      // I2 (QA IA 2026-07-13): persistir el contexto de la cadena — un F5 a
      // mitad de asistente pierde el ?fromSegment de la URL.
      rememberFromSegment(routeId, segmentId);
      submit(formData, {
        method: 'POST',
        action: `/surveillance-route/${routeId}/new-action?fromSegment=${segmentId}`,
      });
    },
    [submit]
  );

  const openChangeCause = useCallback(
    (target: ChangeSegmentCauseTarget) => {
      const onConfirm = async (payload: ChangePauseCauseConfirmPayload) => {
        // El backend reconcilia vínculos (action/external → desvincular,
        // →external marca pendingExternalLink). El hook toastea éxito/error;
        // si falla, lanza y la cadena de abajo no arranca.
        // Al pasar a «Aviso» el back intenta ligarlo solo a una OT: el aviso
        // dice a cuál, o que queda pendiente (QA guía S1, 15-oct), en vez del
        // genérico. La respuesta no distingue «ambiguo» de «sin candidata»:
        // los dos quedan pendientes y la fila ofrece «Vincular aviso».
        const toAviso =
          payload.pauseCause === 'external-call' &&
          target.currentCause !== 'external-call';
        const updated = await updateSegmentPauseCause(
          {
            segmentId: target.segmentId,
            pauseCause: payload.pauseCause,
            pauseCauseDetail: payload.pauseCauseDetail,
          },
          { silentSuccess: toAviso }
        );
        onSaved?.(target.segmentId);
        // Sin respuesta (encolado sin red) ya avisó el hook de la mutation.
        if (toAviso && updated) {
          const roid = updated.linkedExternalOrder?.roid;
          showSuccessToast(
            roid
              ? i18n.t('pages:surveillance.changeCause.toastLinked', { roid })
              : i18n.t('pages:surveillance.changeCause.toastPending')
          );
        }

        // FR-13.3 / D6: si la causa PASA a `action`, misma cadena que el
        // modal de pausa (P13): asistente de la Actuació básica con auto-link
        // al tramo.
        if (
          payload.pauseCause === 'action' &&
          target.currentCause !== 'action'
        ) {
          launchBasicActionChain(target, payload.pauseCauseDetail);
        }
      };

      dispatch(
        openModal(modalIds.CHANGE_PAUSE_CAUSE, {
          currentCause: target.currentCause,
          currentDetail: target.currentDetail,
          linkedOrderRoid: target.linkedOrderRoid ?? undefined,
          allowedCauses: selectableSegmentCauses(
            target.canChooseAction,
            target.currentCause
          ),
          onConfirm,
        })
      );
    },
    [dispatch, launchBasicActionChain, onSaved, updateSegmentPauseCause]
  );

  return { openChangeCause, launchBasicActionChain };
};
